import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  serverTimestamp 
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import { logUserAction, AUDIT_ACTIONS } from "./auditLogger";
import "./styles.css";
import "./auth-management.css";

const ROLES = [
  { value: "viewer", label: "Viewer", description: "Can view tenders only" },
  { value: "engineer", label: "Engineer", description: "Can edit costing and compliance" },
  { value: "manager", label: "Manager", description: "Can manage tenders and team" },
  { value: "admin", label: "Admin", description: "Full access to all features" },
];

export default function UserManagement() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching users:", error);
      setStatusMessage({ type: "error", text: "Failed to load users" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users by search
  const filteredUsers = users.filter((user) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  // Update user role
  const handleUpdateRole = async (userId, newRole) => {
    try {
      const targetUser = users.find(u => u.id === userId);
      const oldRole = targetUser?.role;
      
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { 
        role: newRole,
        updatedAt: serverTimestamp()
      });
      
      // Log role change
      await logUserAction(
        AUDIT_ACTIONS.USER_ROLE_CHANGE,
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: userProfile?.displayName || currentUser.email
        },
        userId,
        targetUser?.displayName || targetUser?.email || "Unknown User",
        { from: oldRole, to: newRole },
        `Changed role from "${oldRole}" to "${newRole}"`
      );
      
      setStatusMessage({ type: "success", text: "Role updated successfully" });
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      setStatusMessage({ type: "error", text: "Failed to update role" });
    }
  };

  // Toggle user active status
  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const targetUser = users.find(u => u.id === userId);
      // Calculate new status from parameter to avoid race conditions
      const newStatus = !currentStatus;
      const fromStatus = currentStatus ? "active" : "inactive";
      const toStatus = newStatus ? "active" : "inactive";
      
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { 
        isActive: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Log status change with pre-calculated values
      await logUserAction(
        AUDIT_ACTIONS.USER_STATUS_CHANGE,
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: userProfile?.displayName || currentUser.email
        },
        userId,
        targetUser?.displayName || targetUser?.email || "Unknown User",
        { from: fromStatus, to: toStatus },
        `${newStatus ? "Activated" : "Deactivated"} user account`
      );
      
      setStatusMessage({ 
        type: "success", 
        text: `User ${newStatus ? "activated" : "deactivated"} successfully` 
      });
      fetchUsers();
    } catch (error) {
      console.error("Error toggling user status:", error);
      setStatusMessage({ type: "error", text: "Failed to update user status" });
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }
    
    try {
      const targetUser = users.find(u => u.id === userId);
      
      await deleteDoc(doc(db, "users", userId));
      
      // Log user deletion
      await logUserAction(
        AUDIT_ACTIONS.USER_DELETE,
        {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: userProfile?.displayName || currentUser.email
        },
        userId,
        targetUser?.displayName || targetUser?.email || "Unknown User",
        null,
        `Deleted user account: ${targetUser?.email}`
      );
      
      setStatusMessage({ type: "success", text: "User deleted successfully" });
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setStatusMessage({ type: "error", text: "Failed to delete user" });
    }
  };

  // Update user details
  const handleSaveEdit = async () => {
    if (!editingUser) return;
    
    try {
      const userRef = doc(db, "users", editingUser.id);
      await updateDoc(userRef, {
        displayName: editingUser.displayName,
        role: editingUser.role,
        updatedAt: serverTimestamp()
      });
      setStatusMessage({ type: "success", text: "User updated successfully" });
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      setStatusMessage({ type: "error", text: "Failed to update user" });
    }
  };

  // Check if current user has admin access
  const isAdmin = userProfile?.role === "admin";

  if (loading) {
    return (
      <div className="management-container">
        <div className="loading-spinner">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <div className="management-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
        <h1>👥 User Management</h1>
        <p>Manage user accounts and permissions</p>
      </div>

      {statusMessage.text && (
        <div className={`status-message status-${statusMessage.type}`}>
          {statusMessage.type === "success" ? "✅" : "❌"} {statusMessage.text}
        </div>
      )}

      <div className="management-toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="user-count">
          {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Team</th>
              <th>Status</th>
              <th>Subscription</th>
              <th>Joined</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className={!user.isActive ? "inactive-row" : ""}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      {user.displayName?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <span className="user-name">{user.displayName || "N/A"}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  {isAdmin ? (
                    <select
                      className="role-select"
                      value={user.role || "viewer"}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`role-badge role-${user.role || "viewer"}`}>
                      {user.role || "viewer"}
                    </span>
                  )}
                </td>
                <td>
                  <span className="team-badge">
                    {user.teamName || "No Team"}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.isActive !== false ? "active" : "inactive"}`}>
                    {user.isActive !== false ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <span className={`subscription-badge sub-${user.subscription || "free"}`}>
                    {user.subscription || "Free"}
                  </span>
                </td>
                <td>
                  {user.createdAt?.toDate 
                    ? user.createdAt.toDate().toLocaleDateString() 
                    : "N/A"}
                </td>
                {isAdmin && (
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => setEditingUser(user)}
                        title="Edit user"
                      >
                        ✏️
                      </button>
                      <button 
                        className="action-btn toggle-btn"
                        onClick={() => handleToggleActive(user.id, user.isActive !== false)}
                        title={user.isActive !== false ? "Deactivate" : "Activate"}
                      >
                        {user.isActive !== false ? "🚫" : "✅"}
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete user"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="empty-table-message">
            {searchQuery ? "No users match your search" : "No users found"}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit User</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={editingUser.displayName || ""}
                  onChange={(e) => setEditingUser({ 
                    ...editingUser, 
                    displayName: e.target.value 
                  })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingUser.email || ""}
                  disabled
                  className="disabled-input"
                />
                <small>Email cannot be changed</small>
              </div>
              
              <div className="form-group">
                <label>Role</label>
                <select
                  value={editingUser.role || "viewer"}
                  onChange={(e) => setEditingUser({ 
                    ...editingUser, 
                    role: e.target.value 
                  })}
                >
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={() => setEditingUser(null)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="new-btn" 
                  style={{ flex: 1 }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
